import {
  Column,
  PromptCard,
  PromptCardSkeleton,
  PromptTabs,
  Row,
  type Filter,
  type Prompt,
} from "@1js/pl-card-components";
import { Filter24Regular } from "@fluentui/react-icons";
import * as React from "react";
import { useFlightContext } from "../../hooks/useFlightContext";
import { LibraryContext } from "../../pages/prompts/allPromptsLibrary/LibraryContext";
import type { SearchResultsData } from "../../types";
import { PromptDetailModal } from "../PromptDetail/PromptDetailModal.web";
import type { WebPrompt } from "../data";
import { useStyles } from "./AllPrompts.web.styles";
import { EmptyStatePromptResults } from "./EmptyStatePromptResults";
import { Filters } from "./Filters.web";
import { PromptHeadingV2 } from "./PromptHeadingV2";
import { ShowMore } from "./ShowMore.web";
import { useColumnStyle } from "./useColumnStyle.hook";

export type AgentPromptsProps = {
  currentFocusedPromptId: string | null;
  setCurrentFocusedPromptId: (promptId: string) => void;
  isLoading: boolean;
  skeletonCount: number;
  promptsToDisplay: Prompt[];
  promptRefs: React.MutableRefObject<
    Map<string, React.RefObject<HTMLDivElement>>
  >;
  handleCardClick: (prompt: Prompt) => void;
  setLastFocusedPromptId: (promptId: string) => void;
  onMoreClicked: () => void;
  isShowHasMore: boolean;
  hasMore?: boolean;
  handleCardFocus: () => void;
  resultsAnnouncementId: string;
  swipe: {
    setSwipedState: (swiped: boolean) => void;
  };
  dialogOpen: boolean;
  handleDialogClose: () => void;
  setDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
  activePrompt: WebPrompt | undefined;
  morePrompts: WebPrompt[];
  onCopyPromptClicked: ((prompt: Prompt) => void) | undefined;
  onTryInAppBadgeAction?: (
    prompt: Prompt,
    product: string,
    tagRef: React.MutableRefObject<null>
  ) => void;
  isShowFilters: boolean;
  filters?: Filter[] | undefined;
  selectedFacets: Record<string, string[]>;
  handleFilterChange: (
    selectedValues: Record<string, string[]>,
    filters: Filter[]
  ) => void;
  onFilterChanged: (filters: Record<string, string[]>) => void;
  children: React.ReactNode[] | React.ReactNode;
  onFiltersCleared: () => void;
  onFiltersOpenChanged: (isOpen: boolean, filters: Filter[]) => void;
  handleKeyDown: (event: React.KeyboardEvent<HTMLDivElement>) => void;
  onPromptBookmarkToggled: (prompt: Prompt) => Promise<void>;
  isMetaOsV2?: boolean;
  searchResultsData?: SearchResultsData;
  isMetaOsV2HomePage?: boolean;
};

export const AgentPrompts: React.FC<AgentPromptsProps> = ({
  currentFocusedPromptId,
  setCurrentFocusedPromptId,
  isLoading,
  skeletonCount,
  promptsToDisplay,
  promptRefs,
  handleCardClick,
  setLastFocusedPromptId,
  onMoreClicked,
  isShowHasMore,
  hasMore,
  handleCardFocus,
  resultsAnnouncementId,
  dialogOpen,
  handleDialogClose,
  setDialogOpen,
  activePrompt,
  morePrompts,
  onCopyPromptClicked,
  onTryInAppBadgeAction,
  isShowFilters,
  filters,
  handleFilterChange,
  children,
  selectedFacets,
  onFiltersOpenChanged,
  onFiltersCleared,
  handleKeyDown,
  onPromptBookmarkToggled,
  isMetaOsV2,
  searchResultsData,
  isMetaOsV2HomePage,
}: AgentPromptsProps) => {
  const styles = useStyles();

  const { isFlightActive } = useFlightContext();
  const { state } = React.useContext(LibraryContext);

  const appearance = isFlightActive("isMosAppPromptCardV2Enabled")
    ? "web-v3"
    : "web";
  const cardColStyles = useColumnStyle();

  // Only Checking promptsToDisplay for Empty state as there is always results(No no results) on selected filter for agent prompts
  if (isMetaOsV2 && promptsToDisplay.length === 0) {
    return (
      <>
        <PromptHeadingV2
          navItem={PromptTabs.AgentPrompts}
          searchResultsData={searchResultsData}
          isMetaOsV2HomePage={isMetaOsV2HomePage}
        />
        <EmptyStatePromptResults navItem={PromptTabs.AgentPrompts} />
      </>
    );
  }

  return (
    <>
      {isMetaOsV2 && (
        <PromptHeadingV2
          navItem={PromptTabs.AgentPrompts}
          searchResultsData={searchResultsData}
          isMetaOsV2HomePage={isMetaOsV2HomePage}
        />
      )}
      {isShowFilters && (
        <div className={styles.filtersRow}>
          {filters && !isLoading && (
            <>
              {isMetaOsV2 && <Filter24Regular className={styles.filterIcon} />}
              <Filters
                filters={filters}
                selectedFilters={selectedFacets}
                onFilterChanged={handleFilterChange}
                onCleared={onFiltersCleared}
                onOpenChanged={onFiltersOpenChanged}
                isMetaOsV2={isMetaOsV2}
              />
            </>
          )}
          <div className={styles.filtersContainer}></div>
        </div>
      )}

      {children}
      {isLoading ? (
        <Row className={styles.cards} isMetaOsV2={isMetaOsV2}>
          {/* create skeletonCount number of PromptCardSkelton component */}
          {Array.from({ length: skeletonCount }).map((_, index) => (
            <Column key={index} size={3} className={cardColStyles}>
              <PromptCardSkeleton appearance={appearance} />
            </Column>
          ))}
        </Row>
      ) : (
        <>
          <Row
            role="list"
            aria-label="Prompt"
            aria-describedby={resultsAnnouncementId}
            className={styles.cards}
            data-testid="prompt-cards"
            onKeyDown={handleKeyDown}
          >
            {promptsToDisplay.map((prompt: Prompt, idx: number) => {
              if (!promptRefs.current.has(prompt.Id)) {
                promptRefs.current.set(prompt.Id, React.createRef());
              }
              const handleFocus = () => {
                setLastFocusedPromptId(prompt.Id);
                setCurrentFocusedPromptId(prompt.Id);
              };
              return (
                <Column key={prompt.Id} size={3} className={cardColStyles}>
                  <PromptCard
                    appearance={appearance}
                    key={prompt.Id}
                    displayPosition={idx}
                    isPromptFocused={currentFocusedPromptId === prompt.Id}
                    onFocus={handleFocus}
                    prompt={prompt}
                    isFavoriteEnabled={isFlightActive("isPromptLikingEnabled")}
                    isPromptLikingEnabled={isFlightActive(
                      "isPromptLikingEnabled"
                    )}
                    onFavoriteToggled={onPromptBookmarkToggled}
                    onClicked={handleCardClick}
                    isBadgesEnabled={true}
                    ref={promptRefs.current.get(prompt.Id)}
                    aria-posinset={idx + 1}
                    aria-setsize={promptsToDisplay.length}
                    isShowPromptAuthor={true}
                    extensionIconMap={state.extensionIconMap}
                  />
                </Column>
              );
            })}
          </Row>
          <PromptDetailModal
            dialogOpen={dialogOpen}
            setDialogOpen={setDialogOpen}
            handleDialogClose={handleDialogClose}
            prompt={activePrompt as WebPrompt}
            morePrompts={morePrompts}
            onMoreLikeThisClicked={handleCardClick}
            onCopyClicked={onCopyPromptClicked}
            onTryInAppBadgeAction={onTryInAppBadgeAction}
            isShowPromptAuthor={true}
            isPromptCopyLinkEnabled={false}
            isPromptLikingEnabled={isFlightActive("isPromptLikingEnabled")}
            isBookmarkEnabled={isFlightActive("isPromptLikingEnabled")}
            onBookmarkToggled={onPromptBookmarkToggled}
          />
          <ShowMore
            isShowHasMore={isShowHasMore}
            hasMore={hasMore}
            isLoading={isLoading}
            handleCardFocus={handleCardFocus}
            onMoreClicked={onMoreClicked}
          />
        </>
      )}
    </>
  );
};
